"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { resendActivationOtp, verifyActivationOtp, setActivationPassword } from "../api/activate";
import { showError } from "@/lib/error-toast";
import { ROUTES } from "@/config/routes";

export type ActivationStep = "identifier" | "otp" | "password" | "success";

const TOAST_ID = "account-activation";

export function useActivateAccount() {
  const router = useRouter();
  const [step, setStep] = useState<ActivationStep>("identifier");
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [setupToken, setSetupToken] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  function getPayload() {
    const trimmed = identifier.trim();
    if (trimmed.includes("@")) {
      return { email: trimmed };
    }
    return { phone: trimmed };
  }

  async function handleSendOtp(event?: React.FormEvent) {
    if (event) event.preventDefault();
    if (!identifier.trim()) {
      showError(new Error("Please enter your email or phone number"));
      return;
    }

    setIsLoading(true);
    toast.loading("Sending activation code...", { id: TOAST_ID });

    try {
      await resendActivationOtp(getPayload());
      setStep("otp");
      toast.success("Activation code sent successfully", { id: TOAST_ID });
    } catch (error: any) {
      toast.dismiss(TOAST_ID);
      showError(error, "Failed to send activation code");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleVerifyOtp(event: React.FormEvent) {
    event.preventDefault();
    if (!otp.trim()) {
      showError(new Error("Please enter the verification code"));
      return;
    }

    setIsLoading(true);
    toast.loading("Verifying code...", { id: TOAST_ID });

    try {
      const payload = getPayload();
      const result = await verifyActivationOtp({ ...payload, otp: otp.trim() });
      setSetupToken(result.token);
      setStep("password");
      toast.success("Code verified successfully. Set your password now.", { id: TOAST_ID });
    } catch (error: any) {
      toast.dismiss(TOAST_ID);
      showError(error, "Invalid or expired code");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSetPassword(event: React.FormEvent) {
    event.preventDefault();
    if (password.length < 8) {
      showError(new Error("Password must be at least 8 characters long"));
      return;
    }

    if (password !== confirmPassword) {
      showError(new Error("Passwords do not match"));
      return;
    }

    setIsLoading(true);
    toast.loading("Setting your password...", { id: TOAST_ID });

    try {
      const payload = getPayload();
      await setActivationPassword({
        ...payload,
        password,
        token: setupToken,
      });

      setStep("success");
      toast.success("Account activated successfully!", { id: TOAST_ID });

      setTimeout(() => {
        router.replace(ROUTES.DASHBOARD);
        router.refresh();
      }, 2000);
    } catch (error: any) {
      toast.dismiss(TOAST_ID);
      showError(error, "Failed to set password");
    } finally {
      setIsLoading(false);
    }
  }

  function handleBackToIdentifier() {
    setStep("identifier");
    setOtp("");
  }

  return {
    step,
    identifier,
    otp,
    password,
    confirmPassword,
    isLoading,
    setIdentifier,
    setOtp,
    setPassword,
    setConfirmPassword,
    handleSendOtp,
    handleVerifyOtp,
    handleSetPassword,
    handleBackToIdentifier,
  };
}
